import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { getApiUrl, getSupabaseHeaders } from '../../utils/api';
import { 
  Plus, 
  Search, 
  Filter, 
  Grid, 
  List, 
  LogOut, 
  User as UserIcon,
  BarChart3,
  Settings,
  RefreshCw,
  Contact,
  Mail,
  Save,
  Edit3,
  ExternalLink,
  Key,
  FileText,
  ChevronDown,
  TrendingUp
  // BookOpen // Temporarily removed
} from 'lucide-react';
import DashboardForm from './DashboardForm';
import DashboardCard from './DashboardCard';
import AdminLogin from './AdminLogin';
// import BlogManagement from './BlogManagement'; // Temporarily removed
import VisitorAnalytics from '../VisitorAnalytics';
import { getAuthToken, setAuthToken, clearAuthTokens, isAuthenticated as checkAuthentication, initializeSession, isSessionExplicitlyActive } from '../../utils/sessionAuth';
import DeleteModal from './DeleteModal';
import { useContact, usePageContent } from '../../App.js';

// Import useTheme hook from App.js
const useTheme = () => {
  const context = React.useContext(React.createContext());
  // Fallback to check document class if context is not available
  const [theme, setTheme] = useState(() => {
    return document.documentElement.classList.contains('dark') ? 'dark' : 'light';
  });
  
  const toggleTheme = () => {
    const newTheme = theme === 'light' ? 'dark' : 'light';
    setTheme(newTheme);
    localStorage.setItem('theme', newTheme);
    if (newTheme === 'dark') {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  };
  
  return { theme, toggleTheme };
};

const AdminPanel = () => {
  const navigate = useNavigate();
  const { theme, toggleTheme } = useTheme();
  const { contactInfo, updateContactInfo } = useContact();
  const { aboutMeContent, servicesContent, updateAboutMeContent, updateServicesContent } = usePageContent();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState(null);
  const [dashboards, setDashboards] = useState([]);
  const [filteredDashboards, setFilteredDashboards] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [editingDashboard, setEditingDashboard] = useState(null);
  const [viewMode, setViewMode] = useState('grid');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [featuredFilter, setFeaturedFilter] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(12);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [dashboardToDelete, setDashboardToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [highlightedDashboard, setHighlightedDashboard] = useState(null);
  const [activeTab, setActiveTab] = useState('dashboards');
  const location = useLocation();
  const [isEditingContact, setIsEditingContact] = useState(false);
  const [tempContactInfo, setTempContactInfo] = useState(contactInfo);
  const [showChangePasswordModal, setShowChangePasswordModal] = useState(false);
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isEditingAboutMe, setIsEditingAboutMe] = useState(false);
  const [isEditingServices, setIsEditingServices] = useState(false);
  const [tempAboutMeContent, setTempAboutMeContent] = useState(aboutMeContent);
  const [tempServicesContent, setTempServicesContent] = useState(servicesContent);
  const [isSavingAboutMe, setIsSavingAboutMe] = useState(false);
  const [isSavingServices, setIsSavingServices] = useState(false);
  const [showProfileDropdown, setShowProfileDropdown] = useState(false);
  const [deleteError, setDeleteError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);

  // Handle navigation state for setting active tab
  useEffect(() => {
    if (location.state?.activeTab) {
      setActiveTab(location.state.activeTab);
    }
  }, [location.state]);

  // Initialize session and prevent auto-login on page refresh
  useEffect(() => {
    // Check if we're returning from BlogEditor (session should remain active)
    const token = getAuthToken();
    const isReturningFromEditor = location.state?.fromEditor;
    
    if (!isReturningFromEditor) {
      // Only clear session active flag on fresh page load, not when returning from editor
      initializeSession();
    }
    
    if (isReturningFromEditor && token) {
      // When returning from editor, trust the existing session and reactivate it
      sessionStorage.setItem('sessionActive', 'true');
      setIsAuthenticated(true);
      
      // Immediately try to restore user data from sessionStorage
      const savedUser = sessionStorage.getItem('adminUser');
      if (savedUser) {
        try {
          const parsedUser = JSON.parse(savedUser);
          // Handle both old format (full response) and new format (just user data)
          const userData = parsedUser.data || parsedUser;
          setUser(userData);
        } catch (e) {
          console.error('Failed to parse saved user data:', e);
        }
      }
      
      // Verify token and fetch fresh user data in background
      verifyToken(token).catch(() => {
        // If token verification fails, user data is already set from sessionStorage
        console.warn('Token verification failed when returning from editor, using cached user data');
      });
    } else if (token && isSessionExplicitlyActive()) {
       // Only verify token if session is explicitly active (user logged in)
       verifyToken(token).catch(() => {
         // Handle token verification failure for normal login
         console.error('Token verification failed for active session');
       });
    } else if (token && !isSessionExplicitlyActive() && !isReturningFromEditor) {
      // Clear tokens if session is not explicitly active on fresh page load
      clearAuthTokens();
      setIsAuthenticated(false);
      setUser(null);
    }
  }, [location.state]);

  // Fetch data when authenticated
  useEffect(() => {
    if (isAuthenticated) {
      fetchDashboards();
      fetchCategories();
    }
  }, [isAuthenticated]);

  // Update temp content when actual content changes
  useEffect(() => {
    setTempAboutMeContent(aboutMeContent);
  }, [aboutMeContent]);

  useEffect(() => {
    setTempServicesContent(servicesContent);
  }, [servicesContent]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showProfileDropdown && !event.target.closest('.profile-dropdown')) {
        setShowProfileDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showProfileDropdown]);

  // Filter dashboards
  useEffect(() => {
    let filtered = dashboards;

    if (searchTerm) {
      filtered = filtered.filter(dashboard =>
        dashboard.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        dashboard.about.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    if (selectedCategory) {
      filtered = filtered.filter(dashboard => dashboard.category === selectedCategory);
    }

    if (featuredFilter === 'featured') {
      filtered = filtered.filter(dashboard => dashboard.isFeatured);
    } else if (featuredFilter === 'not-featured') {
      filtered = filtered.filter(dashboard => !dashboard.isFeatured);
    }

    setFilteredDashboards(filtered);
    setCurrentPage(1);
  }, [dashboards, searchTerm, selectedCategory, featuredFilter]);

  const verifyToken = async (token) => {
    try {
      const response = await fetch(getApiUrl('auth/me'), {
        headers: {
          ...getSupabaseHeaders(),
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const response_data = await response.json();
        // Extract actual user data from the response
        const userData = response_data.data || response_data;
        setUser(userData);
        setIsAuthenticated(true);
        // Save user data to sessionStorage for fallback when returning from editor
        sessionStorage.setItem('adminUser', JSON.stringify(userData));
        return true;
      } else {
        // Clear all tokens on failed verification
        clearAuthTokens();
        setIsAuthenticated(false);
        setUser(null);
        sessionStorage.removeItem('adminUser');
        throw new Error('Token verification failed');
      }
    } catch (err) {
      // Clear all tokens on error
      clearAuthTokens();
      setIsAuthenticated(false);
      setUser(null);
      sessionStorage.removeItem('adminUser');
      throw err;
    }
  };

  const handleLogin = async (credentials) => {
    try {
      setLoading(true);
      setError(null);

      const response = await fetch(getApiUrl('auth/login', 'POST'), {
        method: 'POST',
        headers: getSupabaseHeaders(),
        body: JSON.stringify(credentials)
      });

      const data = await response.json();

      if (response.ok) {
        // Store token using secure session management
        setAuthToken(data.data.access_token);
        setUser(data.data.user);
        setIsAuthenticated(true);
        // Save user data to sessionStorage for fallback when returning from editor
        sessionStorage.setItem('adminUser', JSON.stringify(data.data.user));
      } else {
        setError(data.detail || 'Login failed');
      }
    } catch (err) {
      console.error('Network error deleting dashboard:', err);
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    // Clear all authentication tokens securely
    clearAuthTokens();
    setIsAuthenticated(false);
    setUser(null);
    setDashboards([]);
    setFilteredDashboards([]);
    // Clear saved user data from sessionStorage
    sessionStorage.removeItem('adminUser');
  };

  const fetchDashboards = async () => {
    try {
      setLoading(true);
      const { getSupabaseHeaders } = await import('../../utils/api');
      
      const response = await fetch(getApiUrl('dashboards?limit=100'), {
        headers: getSupabaseHeaders()
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const responseData = await response.json();
      // Handle Supabase Edge Function response structure
      const dashboardsData = responseData.data || responseData;
      
      // Ensure data is always an array and normalize field names
      if (Array.isArray(dashboardsData)) {
        const normalizedDashboards = dashboardsData.map(d => ({
          ...d,
          // Handle PowerBI link field variations
          powerBIEmbedLink: d.powerBIEmbedLink || d.powerbiembedlink || d['powerBIEmbedLink'],
          // Handle main image field variations
          mainImage: d.mainImage || d.mainimage || d['mainImage'],
          // Handle additional images field variations
          additionalImages: d.additionalImages || d.additionalimages || d['additionalImages'] || [],
          // Handle tools used field variations
          toolsUsed: d.toolsUsed || d.toolsused || d['toolsUsed'] || [],
          // Handle created date field variations
          createdDate: d.createdDate || d.createddate || d['createdDate'] || d.createdAt || d.createdat || d['createdAt']
        }));
        setDashboards(normalizedDashboards);
      } else {
        setDashboards([]);
      }
    } catch (err) {
      console.error('Failed to fetch dashboards:', err);
      // Ensure error is always a string
      const errorMessage = typeof err === 'string' ? err : 'Failed to fetch dashboards';
      setError(errorMessage);
      setDashboards([]); // Set empty array on error
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const { getSupabaseHeaders } = await import('../../utils/api');
      
      const response = await fetch(getApiUrl('dashboards/categories'), {
        headers: getSupabaseHeaders()
      });
      const data = await response.json();
      setCategories(data.categories || []);
    } catch (err) {
      console.error('Failed to fetch categories:', err);
    }
  };

  const handleFormSubmit = async (responseData, dashboardId) => {
    // This function is called when DashboardForm successfully submits
    await fetchDashboards();
    setShowForm(false);
    setEditingDashboard(null);
    
    // Highlight the updated/created dashboard
    const targetId = dashboardId || responseData?.data?.id || responseData?.id;
    if (targetId) {
      setHighlightedDashboard(targetId);
      // Remove highlight after 3 seconds
      setTimeout(() => {
        setHighlightedDashboard(null);
      }, 3000);
    }
  };

  const handleDeleteDashboard = (dashboardId) => {
    // Find the dashboard to get its title for the modal
    const dashboard = dashboards.find(d => (d.id || d._id) === dashboardId);
    setDashboardToDelete({ id: dashboardId, title: dashboard?.title || 'Unknown Dashboard' });
    setShowDeleteModal(true);
    setSuccessMessage(null); // Clear any existing success message
  };

  const confirmDeleteDashboard = async () => {
    if (!dashboardToDelete || !dashboardToDelete.id) {
      setDeleteError('Dashboard ID is required for deletion.');
      return;
    }
    
    try {
      setIsDeleting(true);
      const token = getAuthToken();
      
      if (!token) {
        setDeleteError('Authentication token not found. Please log in again.');
        return;
      }
      
      // Construct headers similar to test-delete-auth.ps1
      const deleteHeaders = {
        'apikey': import.meta.env.VITE_SUPABASE_ANON_KEY || import.meta.env.REACT_APP_SUPABASE_ANON_KEY,
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      };
      
      const response = await fetch(getApiUrl(`delete-dashboard/${dashboardToDelete.id}`, 'DELETE'), {
        method: 'DELETE',
        headers: deleteHeaders
      });

      if (response.ok) {
        const deleteResponse = await response.json();
        console.log('Delete response:', deleteResponse);
        const deletedTitle = dashboardToDelete.title;
        await fetchDashboards();
        setShowDeleteModal(false);
        setDashboardToDelete(null);
        setDeleteError(null);
        setSuccessMessage(`Dashboard "${deletedTitle}" has been successfully deleted.`);
        
        // Scroll to top of the page to show success message
        window.scrollTo({ top: 0, behavior: 'smooth' });
        
        // Auto-hide success message after 5 seconds
        setTimeout(() => {
          setSuccessMessage(null);
        }, 5000);
      } else {
        // Handle error response similar to test-delete-auth.ps1
        try {
          const errorData = await response.json();
          const errorMessage = errorData.message || errorData.error || errorData.detail || 'Failed to delete dashboard';
          console.error('Delete error response:', errorData);
          setDeleteError(errorMessage);
        } catch (parseError) {
          console.error('Failed to parse error response:', parseError);
          setDeleteError(`Failed to delete dashboard (${response.status})`);
        }
      }
    } catch (err) {
      console.error('Network error during dashboard deletion:', err);
      setDeleteError('Network error. Please try again.');
    } finally {
      setIsDeleting(false);
    }
  };

  const cancelDeleteDashboard = () => {
    setShowDeleteModal(false);
    setDashboardToDelete(null);
    setDeleteError(null);
    setSuccessMessage(null);
  };

  const handleToggleFeatured = async (dashboardId) => {
    if (!dashboardId) {
      setError('Dashboard ID is required for updating featured status.');
      return;
    }
    
    try {
      const token = getAuthToken();
      
      const response = await fetch(getApiUrl(`update-dashboard?id=${dashboardId}`, 'PATCH'), {
        method: 'PATCH',
        headers: {
          ...getSupabaseHeaders(),
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        await fetchDashboards();
      } else {
        const errorData = await response.json();
        setError(errorData.detail || 'Failed to toggle featured status');
      }
    } catch (err) {
      setError('Network error. Please try again.');
    }
  };

  const handleEditDashboard = (dashboard) => {
    setEditingDashboard(dashboard);
    setShowForm(true);
  };

  const handleViewDashboard = (slug) => {
    navigate(`/dashboard/${slug}`);
  };

  // Pagination
  const totalPages = Math.ceil(filteredDashboards.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedDashboards = filteredDashboards.slice(startIndex, startIndex + itemsPerPage);

  // Update tempContactInfo when contactInfo changes
  useEffect(() => {
    setTempContactInfo(contactInfo);
  }, [contactInfo]);

  // Contact management functions
  const handleContactSave = () => {
    try {
      updateContactInfo(tempContactInfo);
      setIsEditingContact(false);
      setError(null);
      // Show success message
      console.log('Contact information updated successfully!');
    } catch (error) {
      setError('Failed to save contact information');
      console.error('Error saving contact info:', error);
    }
  };

  const handleContactChange = (field, value) => {
    setTempContactInfo(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleContactCancel = () => {
    setTempContactInfo(contactInfo);
    setIsEditingContact(false);
  };

  // Password change functions
  const [passwordError, setPasswordError] = useState(null);

  const validatePassword = (password) => {
    const minLength = 8;
    const hasUpperCase = /[A-Z]/.test(password);
    const hasLowerCase = /[a-z]/.test(password);
    const hasNumbers = /\d/.test(password);
    const hasSpecialChar = /[!@#$%^&*(),.?":{}|<>]/.test(password);
    
    if (password.length < minLength) {
      return 'Password must be at least 8 characters long';
    }
    if (!hasUpperCase) {
      return 'Password must contain at least one uppercase letter';
    }
    if (!hasLowerCase) {
      return 'Password must contain at least one lowercase letter';
    }
    if (!hasNumbers) {
      return 'Password must contain at least one number';
    }
    if (!hasSpecialChar) {
      return 'Password must contain at least one special character';
    }
    return null;
  };

  const handleChangePassword = async () => {
    // Validate form
    if (!passwordForm.currentPassword || !passwordForm.newPassword || !passwordForm.confirmPassword) {
      setPasswordError('All password fields are required');
      return;
    }

    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      setPasswordError('New passwords do not match');
      return;
    }

    const passwordValidation = validatePassword(passwordForm.newPassword);
    if (passwordValidation) {
      setPasswordError(passwordValidation);
      return;
    }

    setIsChangingPassword(true);
    setPasswordError(null);
    setError(null);

    const token = getAuthToken();
    const apiUrl = getApiUrl('auth/change-password', 'POST');
    // Password change request
    
    const startTime = Date.now(); // Move outside try block for proper scope
    
    try {
      // Add timeout to prevent endless loading
      const controller = new AbortController();
      const timeoutId = setTimeout(() => {
        controller.abort();
      }, 60000); // 60 second timeout
      
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          ...getSupabaseHeaders(),
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          currentPassword: passwordForm.currentPassword,
          newPassword: passwordForm.newPassword
        }),
        signal: controller.signal
      });
      
      clearTimeout(timeoutId);
      const endTime = Date.now();

      const data = await response.json();

      if (response.ok) {
        setShowChangePasswordModal(false);
        setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
        setPasswordError(null);
        setShowCurrentPassword(false);
        setShowNewPassword(false);
        setShowConfirmPassword(false);
        // Show success message (you could add a success state if needed)
      } else {
        // Handle error response properly - ensure it's a string
        let errorMessage = 'Failed to change password';
        if (data && typeof data === 'object') {
          if (typeof data.detail === 'string') {
            errorMessage = data.detail;
          } else if (typeof data.message === 'string') {
            errorMessage = data.message;
          } else if (data.detail && typeof data.detail === 'object') {
            // Handle validation error objects
            errorMessage = Array.isArray(data.detail) 
              ? data.detail.map(err => err.msg || err).join(', ')
              : JSON.stringify(data.detail);
          }
        }
        setPasswordError(errorMessage);
      }
    } catch (err) {
      const endTime = Date.now();
      console.error('Password change error:', err);
      // Handle request failure
      
      // Check if it's a timeout error
      if (err.name === 'AbortError') {
        if (endTime - startTime >= 59000) { // Close to our 60s timeout
          setPasswordError('Request timed out after 60 seconds. The server may be slow or unavailable.');
        } else {
          setPasswordError('Request was aborted unexpectedly. This may be a network issue.');
        }
      } else if (err.name === 'SyntaxError') {
        setPasswordError('Invalid response from server. Please try again.');
      } else if (err.message.includes('fetch')) {
        setPasswordError('Network error. Please check your connection and try again.');
      } else {
        setPasswordError('An unexpected error occurred. Please try again.');
      }
    } finally {
      setIsChangingPassword(false);
    }
  };

  const handlePasswordFormChange = (field, value) => {
    setPasswordForm(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleCancelPasswordChange = () => {
    setShowChangePasswordModal(false);
    setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
    setPasswordError(null);
    setError(null);
    setShowCurrentPassword(false);
    setShowNewPassword(false);
    setShowConfirmPassword(false);
  };

  if (!isAuthenticated) {
    return (
      <AdminLogin 
        onLogin={handleLogin} 
        isLoading={loading} 
        error={error} 
      />
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 pt-20">
      {/* Header */}
      <header className="fixed top-20 left-0 right-0 z-40 bg-white dark:bg-gray-800 shadow-sm border-b dark:border-gray-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 sm:py-5">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 sm:gap-4">
              <div className="bg-blue-600 rounded-lg p-1.5 sm:p-2">
                <BarChart3 className="text-white" size={20} />
              </div>
              <div className="hidden sm:block">
                <h1 className="text-xl font-bold text-gray-900 dark:text-white">Admin Panel</h1>
                <p className="text-sm text-gray-600 dark:text-gray-300">Manage your portfolio and contact information</p>
              </div>
              <div className="sm:hidden">
                <h1 className="text-lg font-bold text-gray-900 dark:text-white">Admin Panel</h1>
              </div>
            </div>
            
            {/* Desktop Tab Navigation */}
            <div className="flex items-center gap-1 bg-gray-100 dark:bg-gray-700 rounded-lg p-1 overflow-x-auto">
              <button
                onClick={() => setActiveTab('dashboards')}
                className={`flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium transition-all ${
                  activeTab === 'dashboards'
                    ? 'bg-white dark:bg-gray-600 text-blue-600 dark:text-blue-400 shadow-sm'
                    : 'text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white'
                }`}
              >
                <BarChart3 size={16} />
                Dashboards
              </button>
              <button
                onClick={() => setActiveTab('contacts')}
                className={`flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium transition-all ${
                  activeTab === 'contacts'
                    ? 'bg-white dark:bg-gray-600 text-blue-600 dark:text-blue-400 shadow-sm'
                    : 'text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white'
                }`}
              >
                <Contact size={16} />
                Contact Info
              </button>
              <button
                onClick={() => setActiveTab('about-me')}
                className={`flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium transition-all ${
                  activeTab === 'about-me'
                    ? 'bg-white dark:bg-gray-600 text-blue-600 dark:text-blue-400 shadow-sm'
                    : 'text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white'
                }`}
              >
                <UserIcon size={16} />
                About Me
              </button>
              <button
                onClick={() => setActiveTab('services')}
                className={`flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium transition-all ${
                  activeTab === 'services'
                    ? 'bg-white dark:bg-gray-600 text-blue-600 dark:text-blue-400 shadow-sm'
                    : 'text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white'
                }`}
              >
                <Settings size={16} />
                Services
              </button>
              <button
                onClick={() => setActiveTab('analytics')}
                className={`flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium transition-all ${
                  activeTab === 'analytics'
                    ? 'bg-white dark:bg-gray-600 text-blue-600 dark:text-blue-400 shadow-sm'
                    : 'text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white'
                }`}
              >
                <TrendingUp size={16} />
                Analytics
              </button>
              {/* Blog tab temporarily removed */}
              {/* <button
                onClick={() => setActiveTab('blog')}
                className={`flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium transition-all ${
                  activeTab === 'blog'
                    ? 'bg-white dark:bg-gray-600 text-blue-600 dark:text-blue-400 shadow-sm'
                    : 'text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white'
                }`}
              >
                <BookOpen size={16} />
                Blog
              </button> */}
            </div>
            
            {/* Profile Dropdown */}
             <div className="relative profile-dropdown">
              <button
                onClick={() => setShowProfileDropdown(!showProfileDropdown)}
                className="flex items-center gap-1 sm:gap-2 px-2 sm:px-3 py-2 text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white transition-colors rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700"
              >
                <UserIcon size={16} />
                <span className="text-sm hidden sm:inline">{user?.name}</span>
                <span className="text-xs sm:hidden">Menu</span>
                <ChevronDown size={14} className={`transition-transform ${showProfileDropdown ? 'rotate-180' : ''}`} />
              </button>
              
              {/* Dropdown Menu */}
              <AnimatePresence>
                {showProfileDropdown && (
                  <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    className="absolute right-0 mt-2 w-48 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 py-1 z-50"
                  >
                    {/* Mobile Navigation Items */}
                    <div className="hidden border-b border-gray-200 dark:border-gray-700 pb-1 mb-1">
                      <button
                        onClick={() => {
                          setActiveTab('dashboards');
                          setShowProfileDropdown(false);
                        }}
                        className={`w-full flex items-center gap-2 px-4 py-2 text-sm transition-colors ${
                          activeTab === 'dashboards'
                            ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400'
                            : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                        }`}
                      >
                        <BarChart3 size={16} />
                        Dashboards
                      </button>
                      <button
                        onClick={() => {
                          setActiveTab('contacts');
                          setShowProfileDropdown(false);
                        }}
                        className={`w-full flex items-center gap-2 px-4 py-2 text-sm transition-colors ${
                          activeTab === 'contacts'
                            ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400'
                            : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                        }`}
                      >
                        <Contact size={16} />
                        Contact Info
                      </button>
                      <button
                        onClick={() => {
                          setActiveTab('about-me');
                          setShowProfileDropdown(false);
                        }}
                        className={`w-full flex items-center gap-2 px-4 py-2 text-sm transition-colors ${
                          activeTab === 'about-me'
                            ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400'
                            : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                        }`}
                      >
                        <UserIcon size={16} />
                        About Me
                      </button>
                      <button
                        onClick={() => {
                          setActiveTab('services');
                          setShowProfileDropdown(false);
                        }}
                        className={`w-full flex items-center gap-2 px-4 py-2 text-sm transition-colors ${
                          activeTab === 'services'
                            ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400'
                            : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                        }`}
                      >
                        <Settings size={16} />
                        Services
                      </button>
                      <button
                        onClick={() => {
                          setActiveTab('analytics');
                          setShowProfileDropdown(false);
                        }}
                        className={`w-full flex items-center gap-2 px-4 py-2 text-sm transition-colors ${
                          activeTab === 'analytics'
                            ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400'
                            : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                        }`}
                      >
                        <TrendingUp size={16} />
                        Analytics
                      </button>
                    </div>
                    
                    {/* Profile Actions */}
                    <button
                      onClick={() => {
                        setShowChangePasswordModal(true);
                        setShowProfileDropdown(false);
                      }}
                      className="w-full flex items-center gap-2 px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                    >
                      <Key size={16} />
                      Change Password
                    </button>
                    <button
                      onClick={() => {
                        handleLogout();
                        setShowProfileDropdown(false);
                      }}
                      className="w-full flex items-center gap-2 px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                    >
                      <LogOut size={16} />
                      Logout
                    </button>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>
        </div>
      </header>



      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-8 pt-32">
        {/* Error Message */}
        {error && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-300 px-4 py-3 rounded-lg mb-6 flex items-center justify-between"
          >
            <span>{error}</span>
            <button onClick={() => setError(null)} className="text-red-500 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300">
              ×
            </button>
          </motion.div>
        )}

        {successMessage && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 text-green-700 dark:text-green-300 px-4 py-3 rounded-lg mb-6 flex items-center justify-between"
          >
            <span>{successMessage}</span>
            <button onClick={() => setSuccessMessage(null)} className="text-green-500 dark:text-green-400 hover:text-green-700 dark:hover:text-green-300">
              ×
            </button>
          </motion.div>
        )}

        {/* Dashboard Form */}
        <AnimatePresence>
          {showForm && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black bg-opacity-50 z-50 overflow-y-auto"
            >
              <div className="flex justify-center min-h-full p-2 sm:p-4">
                <div className="w-full max-w-5xl my-8 sm:my-8 mb-24 sm:mb-16">
                <DashboardForm
                  dashboard={editingDashboard}
                  onSave={handleFormSubmit}
                  onCancel={() => {
                    setShowForm(false);
                    setEditingDashboard(null);
                  }}
                  isLoading={loading}
                />
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Change Password Modal */}
        <AnimatePresence>
          {showChangePasswordModal && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-6"
            >
              <motion.div
                initial={{ scale: 0.95, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.95, opacity: 0 }}
                className="bg-white dark:bg-gray-800 rounded-lg shadow-xl p-6 w-full max-w-md"
              >
                <div className="flex items-center gap-3 mb-6">
                  <div className="bg-blue-600 rounded-lg p-2">
                    <Key className="text-white" size={20} />
                  </div>
                  <div>
                    <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Change Password</h2>
                    <p className="text-sm text-gray-600 dark:text-gray-300">Update your account password</p>
                  </div>
                </div>

                {passwordError && (
                  <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-300 px-4 py-3 rounded-lg mb-4 flex items-center justify-between">
                    <span className="text-sm">{passwordError}</span>
                    <button onClick={() => setPasswordError(null)} className="text-red-500 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300">
                      ×
                    </button>
                  </div>
                )}

                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Current Password
                    </label>
                    <div className="relative">
                      <input
                        type={showCurrentPassword ? "text" : "password"}
                        value={passwordForm.currentPassword}
                        onChange={(e) => handlePasswordFormChange('currentPassword', e.target.value)}
                        className="w-full px-3 py-2 pr-10 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="Enter current password"
                      />
                      <button
                        type="button"
                        onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                        className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300"
                      >
                        {showCurrentPassword ? (
                          <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.878 9.878L3 3m6.878 6.878L21 21" />
                          </svg>
                        ) : (
                          <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                          </svg>
                        )}
                      </button>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      New Password
                    </label>
                    <div className="relative">
                      <input
                        type={showNewPassword ? "text" : "password"}
                        value={passwordForm.newPassword}
                        onChange={(e) => handlePasswordFormChange('newPassword', e.target.value)}
                        className="w-full px-3 py-2 pr-10 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="Enter new password (min 8 characters)"
                      />
                      <button
                        type="button"
                        onClick={() => setShowNewPassword(!showNewPassword)}
                        className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300"
                      >
                        {showNewPassword ? (
                          <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.878 9.878L3 3m6.878 6.878L21 21" />
                          </svg>
                        ) : (
                          <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                          </svg>
                        )}
                      </button>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Confirm New Password
                    </label>
                    <div className="relative">
                      <input
                        type={showConfirmPassword ? "text" : "password"}
                        value={passwordForm.confirmPassword}
                        onChange={(e) => handlePasswordFormChange('confirmPassword', e.target.value)}
                        className="w-full px-3 py-2 pr-10 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="Confirm new password"
                      />
                      <button
                        type="button"
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300"
                      >
                        {showConfirmPassword ? (
                          <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.878 9.878L3 3m6.878 6.878L21 21" />
                          </svg>
                        ) : (
                          <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                          </svg>
                        )}
                      </button>
                    </div>
                  </div>
                </div>

                <div className="flex gap-3 mt-6">
                  <button
                    onClick={handleCancelPasswordChange}
                    className="flex-1 px-4 py-2 text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-lg transition-colors"
                    disabled={isChangingPassword}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleChangePassword}
                    disabled={isChangingPassword}
                    className="flex-1 px-4 py-2 bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg transition-colors flex items-center justify-center gap-2"
                  >
                    {isChangingPassword ? (
                      <>
                        <RefreshCw size={16} className="animate-spin" />
                        Changing...
                      </>
                    ) : (
                      'Change Password'
                    )}
                  </button>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Contact Management Section */}
        {activeTab === 'contacts' && (
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6 mb-8">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Contact Information</h2>
                <p className="text-sm text-gray-600 dark:text-gray-300">Manage your social media and contact links</p>
              </div>
              <button
                onClick={() => setIsEditingContact(!isEditingContact)}
                className="flex items-center gap-2 px-4 py-2 rounded-lg transition-colors bg-blue-600 text-white hover:bg-blue-700"
              >
                <Edit3 size={16} />
                Edit
              </button>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* LinkedIn */}
              <div className="space-y-2">
                <label className="flex items-center gap-2 text-sm font-medium text-gray-700 dark:text-gray-300">
                  <svg className="w-4 h-4 text-blue-600" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                  </svg>
                  LinkedIn Profile
                </label>
                {isEditingContact ? (
                  <input
                    type="url"
                    value={tempContactInfo.linkedin}
                    onChange={(e) => handleContactChange('linkedin', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="https://linkedin.com/in/your-profile"
                  />
                ) : (
                  <div className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                    <span className="text-sm text-gray-600 dark:text-gray-300 truncate">{contactInfo?.linkedin || 'Not set'}</span>
                    {contactInfo?.linkedin && (
                      <a
                        href={contactInfo.linkedin}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300"
                      >
                        <ExternalLink size={16} />
                      </a>
                    )}
                  </div>
                )}
              </div>
              
              {/* WhatsApp */}
              <div className="space-y-2">
                <label className="flex items-center gap-2 text-sm font-medium text-gray-700 dark:text-gray-300">
                  <svg className="w-4 h-4 text-green-600" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.89 3.488"/>
                  </svg>
                  WhatsApp Mobile Number
                </label>
                {isEditingContact ? (
                  <input
                    type="tel"
                    value={tempContactInfo.whatsapp}
                    onChange={(e) => handleContactChange('whatsapp', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="971552342984"
                  />
                ) : (
                  <div className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                    <span className="text-sm text-gray-600 dark:text-gray-300 truncate">{contactInfo?.whatsapp || 'Not set'}</span>
                    {contactInfo?.whatsapp && (
                      <a
                        href={`https://api.whatsapp.com/send/?phone=${contactInfo.whatsapp}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-green-600 hover:text-green-700 dark:text-green-400 dark:hover:text-green-300"
                      >
                        <ExternalLink size={16} />
                      </a>
                    )}
                  </div>
                )}
              </div>
              
              {/* Microsoft Fabric Community */}
              <div className="space-y-2">
                <label className="flex items-center gap-2 text-sm font-medium text-gray-700 dark:text-gray-300">
                  <svg className="w-4 h-4 text-purple-600" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M0 0v11.408h11.408V0H0zm12.594 0v11.408H24V0H12.594zM0 12.594V24h11.408V12.594H0zm12.594 0V24H24V12.594H12.594z"/>
                  </svg>
                  Microsoft Fabric Community
                </label>
                {isEditingContact ? (
                  <input
                    type="url"
                    value={tempContactInfo.fabricCommunity}
                    onChange={(e) => handleContactChange('fabricCommunity', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="https://community.fabric.microsoft.com/..."
                  />
                ) : (
                  <div className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                    <span className="text-sm text-gray-600 dark:text-gray-300 truncate">{contactInfo?.fabricCommunity || 'Not set'}</span>
                    {contactInfo?.fabricCommunity && (
                      <a
                        href={contactInfo.fabricCommunity}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-purple-600 hover:text-purple-700 dark:text-purple-400 dark:hover:text-purple-300"
                      >
                        <ExternalLink size={16} />
                      </a>
                    )}
                  </div>
                )}
              </div>
              
              {/* Email */}
              <div className="space-y-2">
                <label className="flex items-center gap-2 text-sm font-medium text-gray-700 dark:text-gray-300">
                  <Mail className="w-4 h-4 text-gray-600" />
                  Email Address
                </label>
                {isEditingContact ? (
                  <input
                    type="email"
                    value={tempContactInfo.email}
                    onChange={(e) => handleContactChange('email', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="your.email@example.com"
                  />
                ) : (
                  <div className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                    <span className="text-sm text-gray-600 dark:text-gray-300 truncate">{contactInfo?.email || 'Not set'}</span>
                    {contactInfo?.email && (
                      <a
                        href={`mailto:${contactInfo?.email || 'raaz.iglesias@gmail.com'}?subject=Collaboration%20Opportunity%20for%20a%20Project&body=Hi%20Arbaz%2C%0D%0A%0D%0AI%20recently%20visited%20your%20portfolio%20website%20and%20was%20very%20impressed%20with%20your%20work%2C%20especially%20your%20approach%20to%20data%20visualization%20and%20dashboard%20design.%0D%0A%0D%0AI%27m%20reaching%20out%20to%20discuss%20a%20potential%20project%20collaboration%20and%20would%20love%20to%20connect%20with%20you%20to%20explore%20how%20we%20can%20work%20together.%0D%0A%0D%0APlease%20let%20me%20know%20a%20convenient%20time%20for%20a%20quick%20call%20or%20email%20exchange.%0D%0A%0D%0ALooking%20forward%20to%20hearing%20from%20you.%0D%0A%0D%0ABest%20regards%2C%0D%0A%5BYour%20Name%5D`}
                        className="text-gray-600 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300"
                      >
                        <Mail size={16} />
                      </a>
                    )}
                  </div>
                )}
              </div>
            </div>
            
            {isEditingContact && (
              <div className="mt-6 flex justify-end gap-3">
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={handleContactCancel}
                  className="px-6 py-2.5 text-gray-600 dark:text-gray-300 bg-gradient-to-r from-gray-100 to-gray-200 dark:from-gray-700 dark:to-gray-600 hover:from-gray-200 hover:to-gray-300 dark:hover:from-gray-600 dark:hover:to-gray-500 rounded-xl transition-all duration-200 font-medium shadow-sm hover:shadow-md"
                >
                  Cancel
                </motion.button>
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={handleContactSave}
                  className="bg-gradient-to-r from-green-600 to-green-700 text-white px-6 py-2.5 rounded-xl hover:from-green-700 hover:to-green-800 transition-all duration-200 flex items-center gap-2 font-medium shadow-lg hover:shadow-xl"
                >
                  <Save size={16} />
                  Save Changes
                </motion.button>
              </div>
            )}
          </div>
        )}
        
        {/* About Me Content Management */}
        {activeTab === 'about-me' && (
          <div className="space-y-6">
            {/* Header */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-xl font-semibold text-gray-900 dark:text-white">About Me Content Management</h2>
                  <p className="text-sm text-gray-600 dark:text-gray-300 mt-1">Manage all content for the About Me page</p>
                </div>
                <button
                   onClick={() => setIsEditingAboutMe(!isEditingAboutMe)}
                   className="flex items-center gap-2 px-4 py-2 rounded-lg transition-colors bg-blue-600 text-white hover:bg-blue-700"
                 >
                   <Edit3 size={16} />
                   {isEditingAboutMe ? 'Cancel Edit' : 'Edit Content'}
                 </button>
              </div>
            </div>

            {/* Introduction Section */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6">
              <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">Introduction</h3>
              {isEditingAboutMe ? (
                 <textarea
                   value={tempAboutMeContent?.intro || ''}
                   onChange={(e) => setTempAboutMeContent(prev => ({ ...prev, intro: e.target.value }))}
                   rows={4}
                   className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                   placeholder="Enter introduction text..."
                 />
              ) : (
                <div className="p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
                  <p className="text-gray-700 dark:text-gray-300">
                    {aboutMeContent?.intro || 'No introduction content set'}
                  </p>
                </div>
              )}
            </div>

            {/* Professional Journey Section */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6">
              <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">Professional Journey</h3>
              {isEditingAboutMe ? (
                 <textarea
                   value={tempAboutMeContent?.professional_journey || ''}
                   onChange={(e) => setTempAboutMeContent(prev => ({ ...prev, professional_journey: e.target.value }))}
                   rows={8}
                   className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                   placeholder="Enter professional journey content (HTML tags supported)..."
                 />
              ) : (
                <div className="p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
                  <div className="text-gray-700 dark:text-gray-300 prose dark:prose-invert max-w-none" 
                       dangerouslySetInnerHTML={{ __html: aboutMeContent?.professional_journey || 'No professional journey content set' }} />
                </div>
              )}
            </div>

            {/* Skills Section */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-900 dark:text-white">Technical Skills</h3>
                {isEditingAboutMe && (
                  <button
                    onClick={() => {
                      const newSkill = { name: '', level: 80 };
                      setTempAboutMeContent(prev => ({
                        ...prev,
                        skills: [...(prev?.skills || []), newSkill]
                      }));
                    }}
                    className="flex items-center gap-2 px-3 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                  >
                    <Plus size={16} />
                    Add Skill
                  </button>
                )}
              </div>
              
              <div className="space-y-3">
                {(isEditingAboutMe ? tempAboutMeContent?.skills : aboutMeContent?.skills)?.map((skill, index) => (
                  <div key={index} className="flex items-center gap-4 p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                    {isEditingAboutMe ? (
                      <>
                        <input
                          type="text"
                          value={skill.name}
                          onChange={(e) => {
                            const updatedSkills = [...(tempAboutMeContent?.skills || [])];
                            updatedSkills[index] = { ...skill, name: e.target.value };
                            setTempAboutMeContent(prev => ({ ...prev, skills: updatedSkills }));
                          }}
                          className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                          placeholder="Skill name"
                        />
                        <input
                          type="range"
                          min="0"
                          max="100"
                          value={skill.level}
                          onChange={(e) => {
                            const updatedSkills = [...(tempAboutMeContent?.skills || [])];
                            updatedSkills[index] = { ...skill, level: parseInt(e.target.value) };
                            setTempAboutMeContent(prev => ({ ...prev, skills: updatedSkills }));
                          }}
                          className="w-32"
                        />
                        <span className="text-sm font-medium text-gray-600 dark:text-gray-300 w-12">{skill.level}%</span>
                        <button
                          onClick={() => {
                            const updatedSkills = tempAboutMeContent?.skills?.filter((_, i) => i !== index) || [];
                            setTempAboutMeContent(prev => ({ ...prev, skills: updatedSkills }));
                          }}
                          className="text-red-600 hover:text-red-700 p-1"
                        >
                          ×
                        </button>
                      </>
                    ) : (
                      <>
                        <span className="flex-1 font-medium text-gray-900 dark:text-white">{skill.name}</span>
                        <div className="flex-1 bg-gray-200 dark:bg-gray-600 rounded-full h-2">
                          <div 
                            className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                            style={{ width: `${skill.level}%` }}
                          />
                        </div>
                        <span className="text-sm font-medium text-gray-600 dark:text-gray-300 w-12">{skill.level}%</span>
                      </>
                    )}
                  </div>
                )) || (
                  <p className="text-gray-500 dark:text-gray-400 text-center py-4">No skills added yet</p>
                )}
              </div>
            </div>

            {/* Areas of Expertise Section */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-900 dark:text-white">Areas of Expertise</h3>
                {isEditingAboutMe && (
                  <button
                    onClick={() => {
                      const newCategory = { category: '', skills: [''] };
                      setTempAboutMeContent(prev => ({
                        ...prev,
                        areas_of_expertise: [...(prev?.areas_of_expertise || []), newCategory]
                      }));
                    }}
                    className="flex items-center gap-2 px-3 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                  >
                    <Plus size={16} />
                    Add Category
                  </button>
                )}
              </div>
              
              <div className="space-y-4">
                {(isEditingAboutMe ? tempAboutMeContent?.areas_of_expertise : aboutMeContent?.areas_of_expertise)?.map((category, categoryIndex) => (
                  <div key={categoryIndex} className="p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
                    {isEditingAboutMe ? (
                      <>
                        <div className="flex items-center gap-2 mb-3">
                          <input
                            type="text"
                            value={category.category}
                            onChange={(e) => {
                              const updatedCategories = [...(tempAboutMeContent?.areas_of_expertise || [])];
                              updatedCategories[categoryIndex] = { ...category, category: e.target.value };
                              setTempAboutMeContent(prev => ({ ...prev, areas_of_expertise: updatedCategories }));
                            }}
                            className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                            placeholder="Category name"
                          />
                          <button
                            onClick={() => {
                              const updatedCategories = tempAboutMeContent?.areas_of_expertise?.filter((_, i) => i !== categoryIndex) || [];
                              setTempAboutMeContent(prev => ({ ...prev, areas_of_expertise: updatedCategories }));
                            }}
                            className="text-red-600 hover:text-red-700 p-2"
                          >
                            ×
                          </button>
                        </div>
                        <div className="space-y-2">
                          {category.skills?.map((skill, skillIndex) => (
                            <div key={skillIndex} className="flex items-center gap-2">
                              <input
                                type="text"
                                value={skill}
                                onChange={(e) => {
                                  const updatedCategories = [...(tempAboutMeContent?.areas_of_expertise || [])];
                                  const updatedSkills = [...category.skills];
                                  updatedSkills[skillIndex] = e.target.value;
                                  updatedCategories[categoryIndex] = { ...category, skills: updatedSkills };
                                  setTempAboutMeContent(prev => ({ ...prev, areas_of_expertise: updatedCategories }));
                                }}
                                className="flex-1 px-3 py-1 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                                placeholder="Skill name"
                              />
                              <button
                                onClick={() => {
                                  const updatedCategories = [...(tempAboutMeContent?.areas_of_expertise || [])];
                                  const updatedSkills = category.skills.filter((_, i) => i !== skillIndex);
                                  updatedCategories[categoryIndex] = { ...category, skills: updatedSkills };
                                  setTempAboutMeContent(prev => ({ ...prev, areas_of_expertise: updatedCategories }));
                                }}
                                className="text-red-600 hover:text-red-700 p-1"
                              >
                                ×
                              </button>
                            </div>
                          ))}
                          <button
                            onClick={() => {
                              const updatedCategories = [...(tempAboutMeContent?.areas_of_expertise || [])];
                              const updatedSkills = [...category.skills, ''];
                              updatedCategories[categoryIndex] = { ...category, skills: updatedSkills };
                              setTempAboutMeContent(prev => ({ ...prev, areas_of_expertise: updatedCategories }));
                            }}
                            className="text-blue-600 hover:text-blue-700 text-sm"
                          >
                            + Add Skill
                          </button>
                        </div>
                      </>
                    ) : (
                      <>
                        <h4 className="font-medium text-gray-900 dark:text-white mb-2">{category.category}</h4>
                        <div className="flex flex-wrap gap-2">
                          {category.skills?.map((skill, skillIndex) => (
                            <span key={skillIndex} className="px-3 py-1 bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200 rounded-full text-sm">
                              {skill}
                            </span>
                          ))}
                        </div>
                      </>
                    )}
                  </div>
                )) || (
                  <p className="text-gray-500 dark:text-gray-400 text-center py-4">No areas of expertise added yet</p>
                )}
              </div>
            </div>

            {/* Key Achievements Section */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-900 dark:text-white">Key Achievements & Recognition</h3>
                {isEditingAboutMe && (
                  <button
                    onClick={() => {
                      const newAchievement = { title: '', description: '' };
                      setTempAboutMeContent(prev => ({
                        ...prev,
                        key_achievements: [...(prev?.key_achievements || []), newAchievement]
                      }));
                    }}
                    className="flex items-center gap-2 px-3 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                  >
                    <Plus size={16} />
                    Add Achievement
                  </button>
                )}
              </div>
              
              <div className="space-y-4">
                {(isEditingAboutMe ? tempAboutMeContent?.key_achievements : aboutMeContent?.key_achievements)?.map((achievement, index) => (
                  <div key={index} className="p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
                    {isEditingAboutMe ? (
                      <>
                        <div className="flex items-center gap-2 mb-3">
                          <input
                            type="text"
                            value={achievement.title}
                            onChange={(e) => {
                              const updatedAchievements = [...(tempAboutMeContent?.key_achievements || [])];
                              updatedAchievements[index] = { ...achievement, title: e.target.value };
                              setTempAboutMeContent(prev => ({ ...prev, key_achievements: updatedAchievements }));
                            }}
                            className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                            placeholder="Achievement title"
                          />
                          <button
                            onClick={() => {
                              const updatedAchievements = tempAboutMeContent?.key_achievements?.filter((_, i) => i !== index) || [];
                              setTempAboutMeContent(prev => ({ ...prev, key_achievements: updatedAchievements }));
                            }}
                            className="text-red-600 hover:text-red-700 p-2"
                          >
                            ×
                          </button>
                        </div>
                        <textarea
                          value={achievement.description}
                          onChange={(e) => {
                            const updatedAchievements = [...(tempAboutMeContent?.key_achievements || [])];
                            updatedAchievements[index] = { ...achievement, description: e.target.value };
                            setTempAboutMeContent(prev => ({ ...prev, key_achievements: updatedAchievements }));
                          }}
                          rows={3}
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                          placeholder="Achievement description"
                        />
                      </>
                    ) : (
                      <>
                        <h4 className="font-medium text-gray-900 dark:text-white mb-2">{achievement.title}</h4>
                        <p className="text-gray-700 dark:text-gray-300 text-sm">{achievement.description}</p>
                      </>
                    )}
                  </div>
                )) || (
                  <p className="text-gray-500 dark:text-gray-400 text-center py-4">No achievements added yet</p>
                )}
              </div>
            </div>

            {/* Save/Cancel Buttons */}
            {isEditingAboutMe && (
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6">
                <div className="flex justify-end gap-3">
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={() => {
                      setIsEditingAboutMe(false);
                      setTempAboutMeContent(aboutMeContent);
                    }}
                    className="px-6 py-2.5 text-gray-600 dark:text-gray-300 bg-gradient-to-r from-gray-100 to-gray-200 dark:from-gray-700 dark:to-gray-600 hover:from-gray-200 hover:to-gray-300 dark:hover:from-gray-600 dark:hover:to-gray-500 rounded-xl transition-all duration-200 font-medium shadow-sm hover:shadow-md"
                  >
                    Cancel
                  </motion.button>
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={async () => {
                      setIsSavingAboutMe(true);
                      try {
                        await updateAboutMeContent(tempAboutMeContent);
                        setIsEditingAboutMe(false);
                        setError(null);
                      } catch (err) {
                        setError('Failed to save About Me content');
                      } finally {
                        setIsSavingAboutMe(false);
                      }
                    }}
                    disabled={isSavingAboutMe}
                    className="bg-gradient-to-r from-green-600 to-green-700 text-white px-6 py-2.5 rounded-xl hover:from-green-700 hover:to-green-800 transition-all duration-200 flex items-center gap-2 font-medium shadow-lg hover:shadow-xl disabled:opacity-50"
                  >
                    {isSavingAboutMe ? (
                      <RefreshCw size={16} className="animate-spin" />
                    ) : (
                      <Save size={16} />
                    )}
                    {isSavingAboutMe ? 'Saving...' : 'Save Changes'}
                  </motion.button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Services Content Management */}
        {activeTab === 'services' && (
          <div className="space-y-6">
            {/* Header */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Services Content Management</h2>
                  <p className="text-sm text-gray-600 dark:text-gray-300 mt-1">Manage all content for the Services page</p>
                </div>
                <button
                  onClick={() => setIsEditingServices(!isEditingServices)}
                  className="flex items-center gap-2 px-4 py-2 rounded-lg transition-colors bg-blue-600 text-white hover:bg-blue-700"
                >
                  <Edit3 size={16} />
                  {isEditingServices ? 'Cancel Edit' : 'Edit Content'}
                </button>
              </div>
            </div>

            {/* Services Introduction */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6">
              <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">Services Introduction</h3>
              {isEditingServices ? (
                <textarea
                  value={tempServicesContent?.intro || ''}
                  onChange={(e) => setTempServicesContent(prev => ({ ...prev, intro: e.target.value }))}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Enter services introduction text..."
                />
              ) : (
                <div className="p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
                  <p className="text-gray-700 dark:text-gray-300">
                    {servicesContent?.intro || 'No services introduction set'}
                  </p>
                </div>
              )}
            </div>

            {/* Service Categories */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-900 dark:text-white">Service Categories</h3>
                {isEditingServices && (
                  <button
                    onClick={() => {
                      const newService = {
                        title: '',
                        description: '',
                        features: []
                      };
                      setTempServicesContent(prev => ({
                        ...prev,
                        services: [...(prev?.services || []), newService]
                      }));
                    }}
                    className="flex items-center gap-2 px-3 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                  >
                    <Plus size={16} />
                    Add Service
                  </button>
                )}
              </div>
              
              <div className="space-y-4">
                {(isEditingServices ? tempServicesContent?.services : servicesContent?.services)?.map((service, index) => (
                  <div key={index} className="border border-gray-200 dark:border-gray-600 rounded-lg p-4">
                    {isEditingServices ? (
                      <div className="space-y-3">
                        <div className="flex items-center gap-3">
                          <input
                            type="text"
                            value={service.title}
                            onChange={(e) => {
                              const updatedServices = [...(tempServicesContent?.services || [])];
                              updatedServices[index] = { ...service, title: e.target.value };
                              setTempServicesContent(prev => ({ ...prev, services: updatedServices }));
                            }}
                            className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                            placeholder="Service title"
                          />
                          <button
                            onClick={() => {
                              const updatedServices = tempServicesContent?.services?.filter((_, i) => i !== index) || [];
                              setTempServicesContent(prev => ({ ...prev, services: updatedServices }));
                            }}
                            className="text-red-600 hover:text-red-700 p-2"
                          >
                            ×
                          </button>
                        </div>
                        <textarea
                          value={service.description}
                          onChange={(e) => {
                            const updatedServices = [...(tempServicesContent?.services || [])];
                            updatedServices[index] = { ...service, description: e.target.value };
                            setTempServicesContent(prev => ({ ...prev, services: updatedServices }));
                          }}
                          rows={3}
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                          placeholder="Service description"
                        />
                        
                        {/* Features */}
                        <div>
                          <div className="flex items-center justify-between mb-2">
                            <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Features</label>
                            <button
                              onClick={() => {
                                const updatedServices = [...(tempServicesContent?.services || [])];
                                updatedServices[index] = {
                                  ...service,
                                  features: [...(service.features || []), '']
                                };
                                setTempServicesContent(prev => ({ ...prev, services: updatedServices }));
                              }}
                              className="text-blue-600 hover:text-blue-700 text-sm"
                            >
                              + Add Feature
                            </button>
                          </div>
                          <div className="space-y-2">
                            {service.features?.map((feature, featureIndex) => (
                              <div key={featureIndex} className="flex items-center gap-2">
                                <input
                                  type="text"
                                  value={feature}
                                  onChange={(e) => {
                                    const updatedServices = [...(tempServicesContent?.services || [])];
                                    const updatedFeatures = [...(service.features || [])];
                                    updatedFeatures[featureIndex] = e.target.value;
                                    updatedServices[index] = { ...service, features: updatedFeatures };
                                    setTempServicesContent(prev => ({ ...prev, services: updatedServices }));
                                  }}
                                  className="flex-1 px-3 py-1 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                                  placeholder="Feature description"
                                />
                                <button
                                  onClick={() => {
                                    const updatedServices = [...(tempServicesContent?.services || [])];
                                    const updatedFeatures = service.features?.filter((_, i) => i !== featureIndex) || [];
                                    updatedServices[index] = { ...service, features: updatedFeatures };
                                    setTempServicesContent(prev => ({ ...prev, services: updatedServices }));
                                  }}
                                  className="text-red-600 hover:text-red-700 p-1"
                                >
                                  ×
                                </button>
                              </div>
                            )) || (
                              <p className="text-gray-500 dark:text-gray-400 text-sm">No features added</p>
                            )}
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div>
                        <h4 className="font-medium text-gray-900 dark:text-white mb-2">{service.title}</h4>
                        <p className="text-gray-600 dark:text-gray-300 mb-3">{service.description}</p>
                        {service.features && service.features.length > 0 && (
                          <ul className="list-disc list-inside space-y-1 text-sm text-gray-600 dark:text-gray-300">
                            {service.features.map((feature, featureIndex) => (
                              <li key={featureIndex}>{feature}</li>
                            ))}
                          </ul>
                        )}
                      </div>
                    )}
                  </div>
                )) || (
                  <p className="text-gray-500 dark:text-gray-400 text-center py-4">No services added yet</p>
                )}
              </div>
            </div>

            {/* Save/Cancel Buttons */}
            {isEditingServices && (
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6">
                <div className="flex justify-end gap-3">
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={() => {
                      setIsEditingServices(false);
                      setTempServicesContent(servicesContent);
                    }}
                    className="px-6 py-2.5 text-gray-600 dark:text-gray-300 bg-gradient-to-r from-gray-100 to-gray-200 dark:from-gray-700 dark:to-gray-600 hover:from-gray-200 hover:to-gray-300 dark:hover:from-gray-600 dark:hover:to-gray-500 rounded-xl transition-all duration-200 font-medium shadow-sm hover:shadow-md"
                  >
                    Cancel
                  </motion.button>
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={async () => {
                      setIsSavingServices(true);
                      try {
                        await updateServicesContent(tempServicesContent);
                        setIsEditingServices(false);
                        setError(null);
                      } catch (err) {
                        setError('Failed to save Services content');
                      } finally {
                        setIsSavingServices(false);
                      }
                    }}
                    disabled={isSavingServices}
                    className="bg-gradient-to-r from-green-600 to-green-700 text-white px-6 py-2.5 rounded-xl hover:from-green-700 hover:to-green-800 transition-all duration-200 flex items-center gap-2 font-medium shadow-lg hover:shadow-xl disabled:opacity-50"
                  >
                    {isSavingServices ? (
                      <RefreshCw size={16} className="animate-spin" />
                    ) : (
                      <Save size={16} />
                    )}
                    {isSavingServices ? 'Saving...' : 'Save Changes'}
                  </motion.button>
                </div>
              </div>
            )}
          </div>
        )}
        
        {/* Dashboard Controls */}
        {activeTab === 'dashboards' && (
          <>
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6 mb-8">
          <div className="flex flex-col lg:flex-row gap-4 items-start lg:items-center justify-between">
            {/* Left Controls */}
            <div className="flex flex-col sm:flex-row gap-4 flex-1">
              {/* Search */}
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 dark:text-gray-500" size={16} />
                <input
                  type="text"
                  placeholder="Search dashboards..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 w-full sm:w-64"
                />
              </div>

              {/* Filters */}
              <div className="flex gap-2">
                <select
                  value={selectedCategory}
                  onChange={(e) => setSelectedCategory(e.target.value)}
                  className="px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">All Categories</option>
                  {categories.map(cat => (
                    <option key={cat} value={cat}>{cat}</option>
                  ))}
                </select>

                <select
                  value={featuredFilter}
                  onChange={(e) => setFeaturedFilter(e.target.value)}
                  className="px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="all">All Dashboards</option>
                  <option value="featured">Featured Only</option>
                  <option value="not-featured">Not Featured</option>
                </select>
              </div>
            </div>

            {/* Right Controls */}
            <div className="flex items-center gap-2">
              <motion.button
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.95 }}
                onClick={fetchDashboards}
                className="p-2.5 text-gray-600 dark:text-gray-300 hover:bg-gradient-to-r hover:from-gray-100 hover:to-gray-200 dark:hover:from-gray-700 dark:hover:to-gray-600 rounded-xl transition-all duration-200 shadow-sm hover:shadow-md"
                title="Refresh"
              >
                <RefreshCw size={16} />
              </motion.button>
              
              <div className="flex border border-gray-300 dark:border-gray-600 rounded-xl shadow-sm">
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => setViewMode('grid')}
                  className={`p-2.5 transition-all duration-200 ${viewMode === 'grid' ? 'bg-gradient-to-r from-blue-600 to-blue-700 text-white shadow-md' : 'text-gray-600 dark:text-gray-300 hover:bg-gradient-to-r hover:from-gray-100 hover:to-gray-200 dark:hover:from-gray-700 dark:hover:to-gray-600'} rounded-l-xl`}
                >
                  <Grid size={16} />
                </motion.button>
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => setViewMode('list')}
                  className={`p-2.5 transition-all duration-200 ${viewMode === 'list' ? 'bg-gradient-to-r from-blue-600 to-blue-700 text-white shadow-md' : 'text-gray-600 dark:text-gray-300 hover:bg-gradient-to-r hover:from-gray-100 hover:to-gray-200 dark:hover:from-gray-700 dark:hover:to-gray-600'} rounded-r-xl`}
                >
                  <List size={16} />
                </motion.button>
              </div>
              
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => setShowForm(true)}
                className="bg-gradient-to-r from-blue-600 to-blue-700 text-white px-6 py-2.5 rounded-xl hover:from-blue-700 hover:to-blue-800 transition-all duration-200 flex items-center gap-2 font-medium shadow-lg hover:shadow-xl"
              >
                <Plus size={16} />
                New Dashboard
              </motion.button>
            </div>
          </div>

          {/* Stats */}
          <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
            <div className="flex gap-6 text-sm text-gray-600 dark:text-gray-300">
              <span>Total: {dashboards.length}</span>
              <span>Featured: {dashboards.filter(d => d.isFeatured).length}</span>
              <span>Showing: {filteredDashboards.length}</span>
            </div>
          </div>
            </div>

            {/* Dashboard Grid/List */}
            {loading && dashboards.length === 0 ? (
              <div className="text-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
                <p className="text-gray-600 dark:text-gray-300">Loading dashboards...</p>
              </div>
            ) : filteredDashboards.length === 0 ? (
              <div className="text-center py-12">
                <p className="text-gray-600 dark:text-gray-300 mb-4">No dashboards found</p>
                <button
                  onClick={() => setShowForm(true)}
                  className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-colors"
                >
                  Create Your First Dashboard
                </button>
              </div>
            ) : (
              <>
                <div className={`grid gap-6 ${
                  viewMode === 'grid' 
                    ? 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4' 
                    : 'grid-cols-1'
                }`}>
              {paginatedDashboards.map((dashboard) => (
                <DashboardCard
                  key={dashboard.id || dashboard._id}
                  dashboard={dashboard}
                  onEdit={handleEditDashboard}
                  onDelete={handleDeleteDashboard}
                  onToggleFeatured={handleToggleFeatured}
                  onView={handleViewDashboard}
                  viewMode={viewMode}
                  showActions={true}
                  className={viewMode === 'list' ? 'flex flex-row' : ''}
                  isHighlighted={highlightedDashboard === (dashboard.id || dashboard._id)}
                />
              ))}
            </div>

        {/* Delete Confirmation Modal */}
        <DeleteModal
          isOpen={showDeleteModal}
          onConfirm={confirmDeleteDashboard}
          onClose={cancelDeleteDashboard}
          dashboardTitle={dashboardToDelete?.title}
          isLoading={isDeleting}
          error={deleteError}
          onClearError={() => setDeleteError(null)}
        />

                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="flex justify-center mt-8">
                    <div className="flex gap-2">
                      <motion.button
                        whileHover={{ scale: currentPage === 1 ? 1 : 1.02 }}
                        whileTap={{ scale: currentPage === 1 ? 1 : 0.98 }}
                        onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                        disabled={currentPage === 1}
                        className="px-4 py-2.5 border border-gray-300 dark:border-gray-600 bg-gradient-to-r from-white to-gray-50 dark:from-gray-700 dark:to-gray-600 text-gray-900 dark:text-white rounded-xl disabled:opacity-50 disabled:cursor-not-allowed hover:from-gray-50 hover:to-gray-100 dark:hover:from-gray-600 dark:hover:to-gray-500 transition-all duration-200 font-medium shadow-sm hover:shadow-md"
                      >
                        Previous
                      </motion.button>
                      
                      {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
                        <motion.button
                          key={page}
                          whileHover={{ scale: 1.05 }}
                          whileTap={{ scale: 0.95 }}
                          onClick={() => setCurrentPage(page)}
                          className={`px-4 py-2.5 border rounded-xl transition-all duration-200 font-medium shadow-sm hover:shadow-md ${
                            currentPage === page
                              ? 'bg-gradient-to-r from-blue-600 to-blue-700 text-white border-blue-600 shadow-lg'
                              : 'border-gray-300 dark:border-gray-600 bg-gradient-to-r from-white to-gray-50 dark:from-gray-700 dark:to-gray-600 text-gray-900 dark:text-white hover:from-gray-50 hover:to-gray-100 dark:hover:from-gray-600 dark:hover:to-gray-500'
                          }`}
                        >
                          {page}
                        </motion.button>
                      ))}
                      
                      <motion.button
                        whileHover={{ scale: currentPage === totalPages ? 1 : 1.02 }}
                        whileTap={{ scale: currentPage === totalPages ? 1 : 0.98 }}
                        onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                        disabled={currentPage === totalPages}
                        className="px-4 py-2.5 border border-gray-300 dark:border-gray-600 bg-gradient-to-r from-white to-gray-50 dark:from-gray-700 dark:to-gray-600 text-gray-900 dark:text-white rounded-xl disabled:opacity-50 disabled:cursor-not-allowed hover:from-gray-50 hover:to-gray-100 dark:hover:from-gray-600 dark:hover:to-gray-500 transition-all duration-200 font-medium shadow-sm hover:shadow-md"
                      >
                        Next
                      </motion.button>
                    </div>
                  </div>
                )}
              </>
            )}
          </>        )}        {/* Blog Management Tab - Temporarily Removed */}
        {/* {activeTab === 'blog' && (
          <BlogManagement token={getAuthToken()} />
        )} */}

        {/* Analytics Tab */}
        {activeTab === 'analytics' && (
          <VisitorAnalytics token={getAuthToken()} />
        )}      </main>
    </div>
  );
};

export default AdminPanel;